import packageJson from '../../../../../package.json';
import { User } from '../../models/user';
import { GlobalEnvironmentService } from '../global-environment.service';
import { StateService } from '../state/state.service';
import { Injectable, inject } from '@angular/core';

import LogRocket from 'logrocket';

interface ILogRocketNetworkRequest {
  body?: string;
  credentials?: string;
  headers: Record<string, string | undefined>;
  method: string;
  mode?: string;
  referrer?: string;
  reqId: string;
  url: string;
}

interface ILogRocketNetworkResponse {
  body?: string;
  headers: Record<string, string | undefined>;
  method: string;
  reqId: string;
  status?: number;
  url: string;
}

const ENDPOINTS_WITH_PASSWORD: string[] = ['/auth/v2/token', '/auth/v2/qima-office'];

@Injectable({
  providedIn: 'root',
})
export class LogrocketInitService {
  private readonly _stateService = inject(StateService);
  private readonly _globalEnvironmentService = inject(GlobalEnvironmentService);
  private _initialized = false;

  /**
   * @returns {boolean} True when LogRocket was initialized in this session.
   */
  public init(): boolean {
    if (this._initialized) {
      return true;
    }

    const environmentId = this._globalEnvironmentService.getLogRocketEnvironmentId();

    if (!environmentId || !this._stateService.isUserLoggedIn()) {
      return false;
    }

    LogRocket.init(environmentId, {
      console: {
        shouldAggregateConsoleErrors: true,
      },
      network: {
        requestSanitizer: (request: ILogRocketNetworkRequest): ILogRocketNetworkRequest => this._sanitizeRequest(request),
        responseSanitizer: (response: ILogRocketNetworkResponse): ILogRocketNetworkResponse => this._sanitizeResponse(response),
      },
    });
    this._initialized = true;

    return true;
  }

  /**
   * @param {Readonly<User>} user - Authenticated user to identify in LogRocket.
   * @returns {void} Nothing.
   */
  public identifyUser(user: Readonly<User>): void {
    if (!this.init()) {
      return;
    }

    try {
      LogRocket.identify(user.username, {
        id: user.id,
        name: user.displayName,
        email: user.email,
        roles: user.roles.join(','),
      });
      LogRocket.track('application', { version: packageJson.version });
    } catch {
      // LogRocket may be blocked or unavailable.
    }
  }

  /**
   * Initializes LogRocket and identifies the user when a stored session is restored or after login.
   * @returns {void} Nothing.
   */
  public syncAuthenticatedSession(): void {
    const user = this._stateService.getCurrentUser();

    if (!user) {
      return;
    }

    this.identifyUser(user);
  }

  /**
   * @param {ILogRocketNetworkRequest} request - Captured network request.
   * @returns {ILogRocketNetworkRequest} Sanitized request for LogRocket.
   */
  public sanitizeRequestForLogRocket(request: ILogRocketNetworkRequest): ILogRocketNetworkRequest {
    return this._sanitizeRequest(request);
  }

  /**
   * @param {ILogRocketNetworkResponse} response - Captured network response.
   * @returns {ILogRocketNetworkResponse} Sanitized response for LogRocket.
   */
  public sanitizeResponseForLogRocket(response: ILogRocketNetworkResponse): ILogRocketNetworkResponse {
    return this._sanitizeResponse(response);
  }

  /**
   * @param {ILogRocketNetworkRequest} request - Captured network request.
   * @returns {ILogRocketNetworkRequest} Sanitized request for LogRocket.
   */
  private _sanitizeRequest(request: ILogRocketNetworkRequest): ILogRocketNetworkRequest {
    if (ENDPOINTS_WITH_PASSWORD.some((path: string): boolean => request.url.toLowerCase().includes(path))) {
      request.body = undefined;
    }

    request.headers['Authorization'] = undefined;
    request.headers['authorization'] = undefined;
    request.headers['Cookie'] = undefined;
    request.headers['cookie'] = undefined;
    request.headers['Qima-Connect-Access-Token'] = undefined;

    return request;
  }

  /**
   * @param {ILogRocketNetworkResponse} response - Captured network response.
   * @returns {ILogRocketNetworkResponse} Sanitized response for LogRocket.
   */
  private _sanitizeResponse(response: ILogRocketNetworkResponse): ILogRocketNetworkResponse {
    if (ENDPOINTS_WITH_PASSWORD.some((path: string): boolean => response.url.toLowerCase().includes(path))) {
      response.body = undefined;
    }

    response.headers['authorization'] = undefined;
    response.headers['Authorization'] = undefined;

    return response;
  }
}
